import React, { useCallback, useEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { useParams } from 'react-router-dom';
import { Button, LinearProgress } from '@material-ui/core';
import _ from 'lodash';
import WebTestNowTestResultsPanel from '../../panels/web-test-now-panels/WebTestNowTestResultsPanel';
import { WebTestNowExportMenu } from '../../panels/web-test-now-panels/WebTestNowResultsMainPanel';
import { Actions, Page, PageHeader, PanelContainer } from '../../page-framework';
import { useNavigate } from '../../../utilities/PgmUtilities';
import ReportAction from '../../page-framework/actions/ReportAction';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import { useDataService } from '../../../services/ApiDataService';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import {
    NetTestNowIsLoadingConsumer,
    NetTestNowIsLoadingProvider,
} from '../net-test-now-page/NetTestNowIsLoadingContext';
import { useIsAdminUser } from '../../../stores/SessionStore';
import useUiToasts, { createToastConfig, INFO, ERROR } from '../../../stores/ToastStore';
import { QUICK_TEST_TYPE } from '../../panels/quick-test-panel/QuickTestConstants';

export const pageId = 'quickTests/web';

const TEST_TYPE_API_URL = '/ipm/v1/open/testTypes';
const SERVICE_TESTS_API_URL = '/ngp/v2/service-tests';

export default function WebTestNowResultsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const handleReRunTest = () => {
        navigate('/quickTests/web', { state: { isDetailsPanel: true, testSelection: id } });
    };
    const [testName, setTestName] = useState();
    const [NetTestNowIsLoading, setNetTestNowIsLoading] = useState(false);
    const [resultsState, setResultsState] = useState({ results: undefined, agentsById: {} });
    const [saving, setSaving] = useState(false);
    const dataService = useDataService();
    const [isAdminUser] = useIsAdminUser();
    const [, toastActions] = useUiToasts();
    const goToUrl = '/quickTests/web';
    const apiUrl = '/ipm/v1/admin/quicktests';
    const functionBreadCrumbs = [{ link: goToUrl, text: t`Web Test Now` }];
    const reportingLinkConfig = {
        target: '/quickTests/web',
    };
    const { triggerRef } = useIntervalRefreshTrigger({
        interval: 20000,
    });

    const test = resultsState.results?.test;
    const canSaveAsWebTest =
        isAdminUser &&
        (test?.quickTestType === QUICK_TEST_TYPE.ADVANCED || test?.quickTestType === QUICK_TEST_TYPE.URL);

    const handleSaveAsWebTest = useCallback(async () => {
        if (!test) return;
        setSaving(true);
        try {
            // Check if a test with the same name already exists
            const existingTests = await dataService.get(`${SERVICE_TESTS_API_URL}`, {
                query: JSON.stringify({ name: test.name }),
            });
            if (!_.isEmpty(existingTests?.data || existingTests)) {
                toastActions.createToast(createToastConfig(ERROR, t`Test with name ${test.name} already exists`));
                return;
            }

            // Get Web test type ID
            const testTypes = await dataService.get(TEST_TYPE_API_URL, {
                query: JSON.stringify({ name: 'Web' }),
            });
            const testTypeId = _.get(testTypes, '[0]._id');

            // Build web test object
            const webTest = {
                copiedFrom: test._id,
                scheduling: { interval: 10 * 60 },
                name: test.name,
                agents: { all: false, ids: test.agents?.ids },
                testConfig: test.testConfig,
                type: testTypeId,
                adapterType: test.adapterType,
                traceRoute: { enabled: true, executeEvery: 1 },
            };

            await dataService.post(`${SERVICE_TESTS_API_URL}`, webTest);
            toastActions.createToast(createToastConfig(INFO, t`Saved as Web Test successfully`));
        } catch (err) {
            const message = /^Test with name/i.test(err?.message) ? err.message : t`Failed to save as Web Test`;
            toastActions.createToast(createToastConfig(ERROR, message));
        } finally {
            setSaving(false);
        }
    }, [test, dataService, toastActions]);

    useEffect(() => {
        (async () => {
            const quickTests = await dataService.get(apiUrl);
            const { name } = quickTests?.filter((qTest) => qTest._id === id).pop();
            setTestName(name);
        })();
    }, [dataService, id]);

    const breadcrumbs = useBreadcrumb(
        functionBreadCrumbs,
        null,
        {},
        'name',
        `${testName ? `Results: ${testName}` : 'Test Result'}`,
        {
            reportingLinkConfig,
        }
    );

    return (
        <Page id={pageId}>
            <NetTestNowIsLoadingProvider value={[NetTestNowIsLoading, setNetTestNowIsLoading]}>
                <PageHeader
                    title={t`Web Test Now Result`}
                    breadcrumbs={breadcrumbs}
                    actions={
                        <Actions>
                            <Button onClick={handleReRunTest} disabled={resultsState.results?.testRunning}>
                                <Trans>Re-Run</Trans>
                            </Button>
                            {canSaveAsWebTest && (
                                <Button
                                    onClick={handleSaveAsWebTest}
                                    disabled={resultsState.results?.testRunning || saving}
                                >
                                    <Trans>Save As Web Test</Trans>
                                </Button>
                            )}
                            <WebTestNowExportMenu
                                test={resultsState.results?.test}
                                agentsById={resultsState.agentsById}
                                disabled={resultsState.results?.testRunning}
                            />
                            <ReportAction
                                pageId={pageId}
                                pageTitle={t`Web Test Now Result`}
                                options={{ canSchedule: false }}
                                tooltipPlacement="bottom"
                            />
                        </Actions>
                    }
                />
                <NetTestNowIsLoadingConsumer>
                    {([testNowQueryIsLoading]) => (
                        <>
                            {(testNowQueryIsLoading || resultsState.results?.testRunning) && (
                                <LinearProgress color="secondary" />
                            )}
                        </>
                    )}
                </NetTestNowIsLoadingConsumer>
                <PanelContainer flexHeight>
                    <WebTestNowTestResultsPanel
                        id={id}
                        hideTitle
                        triggerRef={triggerRef}
                        onResultsChange={setResultsState}
                    />
                </PanelContainer>
            </NetTestNowIsLoadingProvider>
        </Page>
    );
}
